import React, { useState, useRef } from 'react';
import { Box, Paper, TextField, Typography, Button,  InputAdornment, Grid } from '@mui/material';
import loginImage from '../assets/Loginbg.webp';
import useResponsive from '../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, sentotplogin, verifyotplogin, resendOtp } from '../services/Apis';
import { useFilter } from '../context/FilterContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LoginMobile = () => {
    const { isSmallScreen } = useResponsive();
    const navigate = useNavigate();
    const { setProfile, setIsLoggedIn ,setToken} = useFilter();
    const [form, setForm] = useState({ phoneNumber: "", otp: ["", "", "", ""] });
    const [showOtp, setShowOtp] = useState(false);
    const [, setError] = useState("");
    const [, setSuccess] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);

    // Add refs for OTP fields
    const otpRefs = useRef([React.createRef(), React.createRef(), React.createRef(), React.createRef()]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Only update if the input is a number or empty string
        if (name === 'phoneNumber' && value !== '' && !/^\d*$/.test(value)) {
            return; // Don't update if not a number
        }
        setForm(prev => ({
            ...prev,
            [name]: name === 'phoneNumber' ? value.replace(/\D/g, '') : value
        }));
        setError("");
        setSuccess("");
    };

    const handleOtpChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...form.otp];
            newOtp[index] = value;
            setForm({ ...form, otp: newOtp });

            // Auto-focus to next input if value entered and not last field
            if (value && index < otpRefs.current.length - 1) {
                otpRefs.current[index + 1].current.focus();
            }
            // If backspace on empty, focus previous
            if (!value && index > 0) {
                otpRefs.current[index - 1].current.focus();
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!showOtp) {
            if (!form.phoneNumber) {
                toast.error("Please enter your mobile number.");
                return;
            }
            if (!/^\d{10}$/.test(form.phoneNumber)) {
                setError("Mobile number must be 10 digits.");
                return;
            }

            try {
                await sentotplogin({ phoneNumber: form.phoneNumber.trim() });
                setShowOtp(true);
                setResendTimer(30); // <-- Start 30s timer after first OTP sent
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to send OTP.";
                if (msg.toLowerCase().includes("not found") || msg.toLowerCase().includes("not registered")) {
                    toast.error(msg);
                } else {
                    setError(msg);
                }
            }
            return;
        }

        if (form.otp.some(d => d === "")) {
            setError("Please enter the full OTP.");
            return;
        }

        try {
            const enteredOtp = form.otp.join('');
            const res = await verifyotplogin({
                phoneNumber: form.phoneNumber.trim(),
                otp: enteredOtp
            });

            if (res.token) {
                setIsLoggedIn(true);setToken(res.token)
                const user = await getUserProfile(res.token);
                if (user) {
                    setProfile({
                        name: user.name || "",
                        phone: user.phoneNumber || "",
                        email: user.email || "",
                        image: user.imageUrl || ""
                    });
                }
                document.cookie = `token=${encodeURIComponent(res.token)}; path=/; max-age=2592000`;
                if (res.name) {
                    document.cookie = `userName=${encodeURIComponent(res.name)}; path=/; max-age=2592000`;
                }
            }
            setSuccess("Login successful!");
            navigate('/');
        } catch (err) {
            const msg = err.response?.data?.message || "Login failed.";
            if (msg.toLowerCase().includes("not found") || msg.toLowerCase().includes("not registered")) {
                toast.error(msg);
            } else {
                setError(msg);
            }
        }
    };

    

   

    const handleResendOtp = async () => {
        setResendLoading(true);
        setError("");
        setSuccess("");
        try {
            await resendOtp({ phoneNumber: form.phoneNumber.trim() });
            setResendTimer(30);
            toast.success("OTP resent successfully!");
        } catch (err) {
            setError("Failed to resend OTP.");
        } finally {
            setResendLoading(false);
        }
    };

    // Countdown timer for resend OTP
    React.useEffect(() => {
        let timer;
        if (resendTimer > 0) {
            timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [resendTimer]);

    return (
        <Box sx={{ width: '100%', minHeight: '75vh', backgroundImage: `url(${loginImage})`, backgroundSize: 'cover', backgroundPosition: 'top', position: 'relative' }}>
            <Box sx={{ position: 'absolute', right: isSmallScreen ? '50%' : '5%', top: '50%', transform: isSmallScreen ? 'translate(50%, -50%)' : 'translateY(-50%)', width: '100%', maxWidth: 360, px: 2 }}>
                <Paper elevation={4} sx={{ p: isSmallScreen ? 2 : 4, borderRadius: 3 }}>
                    <Typography variant="h3" fontWeight={600} align="center" gutterBottom>Login</Typography>
                    <form onSubmit={handleSubmit}>
                        {!showOtp && (
                            <TextField fullWidth placeholder="Mobile Number " name="phoneNumber" value={form.phoneNumber}
                                onChange={handleChange} margin="normal" autoComplete="tel"
                                inputProps={{
                                    inputMode: 'tel',
                                    pattern: '[0-9]*',
                                    maxLength: 10,
                                    type: 'tel'
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <i className="fas fa-mobile-alt" style={{ color: '#888', fontSize: '16px' }} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            
                        )}

                        {showOtp && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" gutterBottom>Enter your OTP</Typography>
                                <Grid container spacing={1} justifyContent="center">
                                    {form.otp.map((digit, index) => (
                                        <Grid item key={index}>
                                            <TextField value={digit} inputRef={otpRefs.current[index]}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                inputProps={{ maxLength: 1, inputMode: 'numeric', style: { textAlign: 'center' } }}
                                                sx={{ width: 60 }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                {/* Add this Box for Resend OTP */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={-1}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: resendTimer > 0 || resendLoading ? '#aaa' : '#CB4422',
                                            cursor: resendTimer > 0 || resendLoading ? 'not-allowed' : 'pointer',
                                            fontWeight: 450,
                                            fontSize: 13    ,
                                            userSelect: 'none'
                                        }}
                                        onClick={() => {
                                            if (resendTimer === 0 && !resendLoading) handleResendOtp();
                                        }}
                                    >
                                        {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                                    </Typography>
                                    
                                </Box>
                            </Box>
                        )}{!showOtp && <Typography
                                        variant="body1"
                                        sx={{ cursor: 'pointer', fontSize: 14 , placeSelf:'end' }}
                                        onClick={() => navigate('/register')}
                                    >
                                        Not Registered?
                                    </Typography>}

                        <Button disableFocusRipple disableTouchRipple disableRipple
                            type="submit" fullWidth variant="contained"
                            sx={{
                                bgcolor: '#e5391f', mt: 2, mb: 2,
                                textTransform: 'none'
                            }}
                        >
                            {showOtp ? "Login" : "Next"}
                        </Button>
                    </form>
                   
                    {/* <Divider>or</Divider>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <GoogleLogin  onSuccess={handleGoogleSuccess}  onError={handleGoogleError} />
                    </Box> */}
                </Paper>
            </Box>
 <ToastContainer position="top-right" />
        </Box>
    );
};

export default LoginMobile;
